import { useCallback, useMemo } from 'react';

import { getCropInfoForQuickCampaign } from 'utils/campaign/utils';
import { CampaignProcess } from 'utils/constants/campaign';

import campaignCreateFlowButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_create_flow_button_click';

import { loadSourceFormConfig } from 'components/CpcFlexibleCreate/CpcForm/AdSources/loadSourceFormConfig';

import { setIsAiGenerationModalVisible } from 'store/models/aiImageGeneration';
import {
    Images,
    resetBannerImages,
    resetBannerImagesToSaved,
    resetBannerUpscaledImages,
    setBannerSavedToImages,
    setIsCropImageModalVisible,
    setIsCropImageModalWasOpenedFromAiImageGenerationModal,
    setIsCropsSaved,
} from 'store/models/createBanner';
import {
    QuickCampaignFieldName,
    setQuickCampaignFieldByName,
    setQuickCampaignIsCropImageModalVisible,
    setQuickCampaignIsCropImageModalWasOpenedFromQuickGeneration,
} from 'store/models/createQuickCampaign';
import { useDispatch, useSelector } from 'store/utils';

interface UseCropImageModal {
    isCropImageModalVisible: boolean;
    images: Images;
    isCropImageModalWasOpenedFromGeneration: boolean;
    resetModal: () => void;
    handleCloseModal: () => void;
    handleCropsSave: () => void;
}

export const useCropImageModal = (isQuickCampaign?: boolean): UseCropImageModal => {
    const dispatch = useDispatch();

    const {
        isCropImageModalVisible: isCreateBannerCropImageModalVisible,
        bannerId,
        images: createBannerImages,
        isCropsSaved,
    } = useSelector((state) => state.createBanner);
    const {
        isCropImageModalVisible: isQuickCampaignCropImageModalVisible,
        images: quickCampaignImages,
        isCropImageModalWasOpenedFromQuickGeneration,
    } = useSelector((state) => state.createQuickCampaign);
    const { campaignProcess } = useSelector((state) => state.createCampaign);
    const isCropImageModalWasOpenedFromAiImageGenerationModal = useSelector(
        (state) => state.createBanner.isCropImageModalWasOpenedFromAiImageGenerationModal
    );

    const isCropImageModalVisible = useMemo(
        () => (isQuickCampaign ? isQuickCampaignCropImageModalVisible : isCreateBannerCropImageModalVisible),
        [isQuickCampaign, isQuickCampaignCropImageModalVisible, isCreateBannerCropImageModalVisible]
    );

    const images = useMemo(
        () => (isQuickCampaign ? quickCampaignImages : createBannerImages),
        [isQuickCampaign, quickCampaignImages, createBannerImages]
    );

    const isCropImageModalWasOpenedFromGeneration = useMemo(
        () =>
            isQuickCampaign
                ? isCropImageModalWasOpenedFromQuickGeneration
                : isCropImageModalWasOpenedFromAiImageGenerationModal,
        [
            isQuickCampaign,
            isCropImageModalWasOpenedFromQuickGeneration,
            isCropImageModalWasOpenedFromAiImageGenerationModal,
        ]
    );

    const changeCropImageOpenedFrom = useMemo(
        () =>
            isQuickCampaign
                ? setQuickCampaignIsCropImageModalWasOpenedFromQuickGeneration
                : setIsCropImageModalWasOpenedFromAiImageGenerationModal,
        [isQuickCampaign]
    );

    const closeModal = useCallback(() => {
        if (isQuickCampaign) {
            dispatch(setQuickCampaignIsCropImageModalVisible(false));
        } else {
            dispatch(setIsCropImageModalVisible(false));
        }
    }, [dispatch, isQuickCampaign]);

    const resetModal = useCallback(() => {
        dispatch(resetBannerImagesToSaved(null));
        dispatch(resetBannerUpscaledImages(null));
        if (!bannerId) {
            campaignCreateFlowButtonClick({ buttonName: 'campaign_create_crop_back' });
        }
        if (!isCropsSaved && !bannerId && campaignProcess !== CampaignProcess.Copy) {
            dispatch(resetBannerImages(null));
        }
    }, [dispatch, bannerId, isCropsSaved, campaignProcess]);

    const handleCloseModal = useCallback(() => {
        closeModal();
        if (isCropImageModalWasOpenedFromGeneration) {
            dispatch([setIsAiGenerationModalVisible(true), changeCropImageOpenedFrom(false)]);
        }
    }, [closeModal, isCropImageModalWasOpenedFromGeneration, dispatch, changeCropImageOpenedFrom]);

    const handleCropsSave = useCallback(() => {
        if (!isQuickCampaign) {
            dispatch([setIsCropsSaved(true), setBannerSavedToImages(null), setIsCropImageModalVisible(false)]);
            // после сохранения новых кропов нужно актуализировать доступные площадки
            void dispatch(loadSourceFormConfig());
        } else {
            dispatch([
                setQuickCampaignFieldByName({
                    fieldName: QuickCampaignFieldName.CroppedImages,
                    value: getCropInfoForQuickCampaign(images),
                }),
                setQuickCampaignIsCropImageModalVisible(false),
            ]);
        }
    }, [dispatch, isQuickCampaign, images]);

    return {
        isCropImageModalVisible,
        images,
        isCropImageModalWasOpenedFromGeneration,
        resetModal,
        handleCloseModal,
        handleCropsSave,
    };
};
